import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Server, AlertCircle, Search, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RefreshIndicator({ lastUpdate, interval }) {
  const formatInterval = (secs) => {
    if (secs >= 60) return `${Math.floor(secs / 60)} min`;
    return `${secs}s`;
  };
  
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <RefreshCw className="h-3 w-3" />
      <span>Auto: {formatInterval(interval)}</span>
      {lastUpdate && (
        <span className="text-muted-foreground/60">• Último: {new Date(lastUpdate).toLocaleTimeString('pt-BR')}</span>
      )}
    </div>
  );
}

function StatusIndicator({ status }) {
  const styles = {
    up: 'bg-green-500',
    warning: 'bg-orange-500',
    down: 'bg-red-500',
    disabled: 'bg-gray-400'
  };
  
  return (
    <div className={cn("w-3 h-3 rounded-full", styles[status] || 'bg-gray-400')} />
  );
}

function SummaryCard({ title, value, icon: Icon, bgColor, textColor }) {
  return (
    <Card className={cn("border-0", bgColor)}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className={cn("text-sm font-medium", textColor || "text-white/80")}>{title}</p>
            <p className={cn("text-4xl font-bold mt-2", textColor || "text-white")}>{value}</p>
          </div>
          <Icon className={cn("h-12 w-12", textColor || "text-white/50")} />
        </div>
      </CardContent>
    </Card>
  );
}

function getAvailabilityColor(value) {
  if (value >= 99) return 'text-green-500';
  if (value >= 95) return 'text-orange-500';
  return 'text-red-500';
}

export function HostsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const { getAuthHeader } = useAuthStore();
  const { refreshInterval } = useThemeStore();
  const navigate = useNavigate();

  // Apply saved filter from sessionStorage
  useEffect(() => {
    try {
      const savedFilter = sessionStorage.getItem('appliedFilter');
      if (savedFilter) {
        const f = JSON.parse(savedFilter);
        if (f.search) setSearchTerm(f.search);
        sessionStorage.removeItem('appliedFilter');
      }
    } catch (err) {
      console.warn('Filtro salvo inválido, ignorando:', err);
      sessionStorage.removeItem('appliedFilter');
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/hosts`, { headers: getAuthHeader() });
      setData(response.data);
      setLastUpdate(new Date().toISOString());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar hosts');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [fetchData, refreshInterval]);

  const filteredHosts = data?.hosts?.filter(host => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      host.name.toLowerCase().includes(term) ||
      host.category.toLowerCase().includes(term) ||
      host.current_trigger.toLowerCase().includes(term) ||
      host.ip?.toLowerCase().includes(term)
    );
  }) || [];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-20 w-full" /></CardContent></Card>
          ))}
        </div>
        <Skeleton className="h-[500px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hosts-page">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Refresh Indicator */}
      <div className="flex justify-end">
        <RefreshIndicator lastUpdate={lastUpdate} interval={refreshInterval} />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard 
          title="Total de Hosts" 
          value={data?.summary?.total || 0} 
          icon={Server} 
          bgColor="bg-slate-800"
        />
        <SummaryCard 
          title="UP" 
          value={data?.summary?.up || 0} 
          icon={CheckCircle2} 
          bgColor="bg-green-600"
        />
        <SummaryCard 
          title="Warning" 
          value={data?.summary?.warning || 0} 
          icon={AlertTriangle} 
          bgColor="bg-orange-500"
        />
        <SummaryCard 
          title="Down" 
          value={data?.summary?.down || 0} 
          icon={XCircle} 
          bgColor="bg-red-600"
        />
        <SummaryCard 
          title="Desabilitados" 
          value={data?.summary?.disabled || 0} 
          icon={Server} 
          bgColor="bg-gray-500"
        />
      </div>

      {/* Search and Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Server className="h-4 w-4" />
              Lista de Hosts ({filteredHosts.length})
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar hosts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-hosts"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea style={{ height: 'calc(100vh - 340px)' }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Status</TableHead>
                  <TableHead>Host</TableHead>
                  <TableHead>IP</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead className="text-right w-[90px]">Disponib.</TableHead>
                  <TableHead className="text-right w-[70px]">Interr.</TableHead>
                  <TableHead className="text-right w-[70px]">MTTR</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHosts.length > 0 ? (
                  filteredHosts.map((host) => (
                    <TableRow 
                      key={host.id}
                      className={cn(
                        "cursor-pointer",
                        host.status === 'warning' && 'bg-orange-500/10 hover:bg-orange-500/20',
                        host.status === 'down' && 'bg-red-500/10 hover:bg-red-500/20',
                        host.status === 'disabled' && 'opacity-50'
                      )}
                      onClick={() => navigate(`/dashboard/hosts/${host.id}`)}
                      data-testid={`host-row-${host.id}`}
                    >
                      <TableCell>
                        <StatusIndicator status={host.status} />
                      </TableCell>
                      <TableCell>
                        <p className="font-medium text-sm">{host.name}</p>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-mono text-muted-foreground">{host.ip || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{host.category}</Badge>
                      </TableCell>
                      <TableCell>
                        {host.current_trigger ? (
                          <p className="text-sm text-red-500 max-w-[250px] truncate" title={host.current_trigger}>
                            {host.current_trigger}
                          </p>
                        ) : (
                          <span className="text-sm text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className={cn("text-right font-semibold", getAvailabilityColor(host.availability))}>
                        {host.availability}%
                      </TableCell>
                      <TableCell className="text-right">
                        {host.interruptions > 0 ? (
                          <Badge variant="outline">{host.interruptions}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">{host.mttr}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/hosts/${host.id}`); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                      {searchTerm ? 'Nenhum host encontrado' : 'Nenhum host cadastrado'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
